import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "@/styles/seller-map.css";
import { GoogleIcon } from "@/components/GoogleIcon";
import { cn } from "@/lib/utils";
import { num } from "@/lib/format";
import US_STATES from "@/data/us-states.json";
import {
  Search, RotateCcw, X, Copy, ExternalLink, ChevronDown, HelpCircle,
  SlidersHorizontal, BarChart3, MapPin, Building2, CheckCircle2, AlertTriangle, Crosshair,
} from "lucide-react";
import { toast } from "sonner";
import { cartoLightAllTileUrl, CARTO_BASEMAP_LAYER_OPTIONS } from "@/lib/cartoBasemap";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;
const US_CENTER = [39.5, -98.35];
const US_ZOOM = 4;

const DEFAULT_FILTERS = {
  name: "", brand: "All", brand_status: "All", state: "All", city: "", zip: "",
  source: "All", website: false, phone: false, contact_verification: "All",
  review_status: "All", precision: "All", include_approximate: true, min_branches: "", limit: 1000,
};

const STATE_ABBR = {
  Alabama: "AL", Alaska: "AK", Arizona: "AZ", Arkansas: "AR", California: "CA", Colorado: "CO",
  Connecticut: "CT", Delaware: "DE", "District of Columbia": "DC", Florida: "FL", Georgia: "GA",
  Hawaii: "HI", Idaho: "ID", Illinois: "IL", Indiana: "IN", Iowa: "IA", Kansas: "KS", Kentucky: "KY",
  Louisiana: "LA", Maine: "ME", Maryland: "MD", Massachusetts: "MA", Michigan: "MI", Minnesota: "MN",
  Mississippi: "MS", Missouri: "MO", Montana: "MT", Nebraska: "NE", Nevada: "NV", "New Hampshire": "NH",
  "New Jersey": "NJ", "New Mexico": "NM", "New York": "NY", "North Carolina": "NC", "North Dakota": "ND",
  Ohio: "OH", Oklahoma: "OK", Oregon: "OR", Pennsylvania: "PA", "Rhode Island": "RI", "South Carolina": "SC",
  "South Dakota": "SD", Tennessee: "TN", Texas: "TX", Utah: "UT", Vermont: "VT", Virginia: "VA",
  Washington: "WA", "West Virginia": "WV", Wisconsin: "WI", Wyoming: "WY", "Puerto Rico": "PR",
};

const BADGE_CLASS = {
  verified_seller: "ez-badge-verified", listed_by_brand: "ez-badge-listed",
  reported: "ez-badge-reported", needs_verification: "ez-badge-needs",
};
const STATUS_LABEL = {
  verified_seller: "Verified seller", listed_by_brand: "Listed in brand verified list",
  reported: "Reported brand", needs_verification: "Needs verification",
};
const PRECISION_LABEL = {
  rooftop: "Exact address", street: "Street-level", postal_code: "ZIP-code area",
  city: "City-level", state: "State-level (review only)",
};

function clusterIcon(cluster) {
  const c = cluster.getChildCount();
  const size = c < 10 ? 34 : c < 50 ? 42 : c < 150 ? 52 : 62;
  return L.divIcon({ html: `<div class="ez-cluster" style="width:${size}px;height:${size}px">${c}</div>`, className: "ez-cluster-wrap", iconSize: L.point(size, size) });
}
function markerIcon(row, selected) {
  const cls = row.reviewOnly ? "review" : (row.geocodingStatus === "approximate" ? "approx" : "");
  return L.divIcon({ className: "ez-seller-wrap", html: `<div class="ez-seller ${cls}${selected ? " sel" : ""}"></div>`, iconSize: L.point(18, 18) });
}
const clean = (v) => (v == null || v === "" ? null : v);
const isUrl = (v) => (typeof v === "string" && /^https?:\/\//i.test(v));

// Build a Google search URL from the strongest available verified fields of the
// SELECTED branch (never placeholders). Returns null only if nothing usable.
function googleUrl({ name, address, city, state, postalCode }) {
  const n = clean(name);
  if (!n) return null;
  let parts;
  if (clean(address) || clean(city) || clean(postalCode)) {
    parts = [n, address, city, state, postalCode];
  } else if (clean(state)) {
    parts = [n, state, "distributor"];
  } else {
    parts = [n];
  }
  const q = parts.filter(Boolean).join(" ");
  return `https://www.google.com/search?q=${encodeURIComponent(q)}`;
}

export default function DistributorMap() {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const clusterRef = useRef(null);
  const geoLayerRef = useRef(null);
  const markerById = useRef({});
  const openBranchId = useRef(null);
  const selectedLayerRef = useRef(null);
  const reqId = useRef(0);

  const [facets, setFacets] = useState({ states: [], sources: [], brands: [], brandStatuses: [], reviewStatuses: [], contactVerifications: [], precisions: [] });
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [status, setStatus] = useState("idle");
  const [meta, setMeta] = useState({ total: 0, returned: 0, placed: 0, approximate: 0, unresolved: 0, companiesMatched: 0 });
  const [stats, setStats] = useState(null);
  const [selectedState, setSelectedState] = useState(null);

  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [mobileCard, setMobileCard] = useState(null); // marker row for mobile compact card
  const [drawer, setDrawer] = useState(null);          // { distributorId, selectedBranchId }
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileTab, setProfileTab] = useState("overview");

  const [openPanel, setOpenPanel] = useState(null);    // filters | help | legend | null
  const panelRef = useRef(null);
  const toggleGroupRef = useRef(null);
  const btnRefs = { filters: useRef(null), help: useRef(null), legend: useRef(null) };

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const on = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", on); return () => window.removeEventListener("resize", on);
  }, []);

  const togglePanel = (p) => setOpenPanel((cur) => (cur === p ? null : p));
  const safeInvalidate = () => {
    const m = mapRef.current;
    if (!m || !m._loaded || !mapEl.current?.offsetParent) return;
    try { m.invalidateSize(); } catch { /* not ready */ }
  };

  // Escape + click-outside for floating panels
  useEffect(() => {
    if (!openPanel) return;
    const onKey = (e) => { if (e.key === "Escape") { const p = openPanel; setOpenPanel(null); btnRefs[p]?.current?.focus(); } };
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (toggleGroupRef.current?.contains(e.target)) return;
      if (e.target.closest?.(".leaflet-popup") || e.target.closest?.("select")) return;
      setOpenPanel(null);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onDown); };
    // eslint-disable-next-line
  }, [openPanel]);
  useEffect(() => { const t = setTimeout(safeInvalidate, 260); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [openPanel]);

  // init map once
  useEffect(() => {
    if (mapRef.current || !mapEl.current) return;
    const map = L.map(mapEl.current, { center: US_CENTER, zoom: US_ZOOM, zoomControl: false, worldCopyJump: true });
    L.control.zoom({ position: "topright" }).addTo(map);
    L.tileLayer(cartoLightAllTileUrl(), CARTO_BASEMAP_LAYER_OPTIONS).addTo(map);

    const geo = L.geoJSON(US_STATES, {
      style: () => ({ color: "#94a3b8", weight: 1, fillColor: "#0f766e", fillOpacity: 0.04 }),
      onEachFeature: (feature, layer) => {
        layer.on({
          mouseover: (e) => { if (selectedLayerRef.current !== e.target) e.target.setStyle({ fillOpacity: 0.12, weight: 1.5 }); },
          mouseout: (e) => { if (selectedLayerRef.current !== e.target) geoLayerRef.current.resetStyle(e.target); },
          click: (e) => onStateClick(feature, e.target),
        });
      },
    }).addTo(map);
    geoLayerRef.current = geo;

    const cluster = L.markerClusterGroup({ iconCreateFunction: clusterIcon, showCoverageOnHover: false, maxClusterRadius: 55, chunkedLoading: true });
    map.addLayer(cluster);
    map.on("popupclose", () => {
      openBranchId.current = null;
      Object.values(markerById.current).forEach(({ mk: m, row: r }) => m.setIcon(markerIcon(r, false)));
    });
    mapRef.current = map; clusterRef.current = cluster;
    setTimeout(safeInvalidate, 120);
    return () => { map.remove(); mapRef.current = null; };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    window.addEventListener("resize", safeInvalidate);
    const t = setInterval(safeInvalidate, 800);
    return () => { window.removeEventListener("resize", safeInvalidate); clearInterval(t); };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetch(`${API}/distributor-map/facets`).then((r) => r.json()).then((d) => setFacets(d.data || d)).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const paramsFromFilters = (f) => {
    const p = new URLSearchParams();
    if (f.name.trim()) p.set("name", f.name.trim());
    if (f.brand !== "All") p.set("brand", f.brand);
    if (f.brand_status !== "All") p.set("brand_status", f.brand_status);
    if (f.state !== "All") p.set("state", f.state);
    if (f.city.trim()) p.set("city", f.city.trim());
    if (f.zip.trim()) p.set("zip", f.zip.trim());
    if (f.source !== "All") p.set("source", f.source);
    if (f.contact_verification !== "All") p.set("contact_verification", f.contact_verification);
    if (f.review_status !== "All") p.set("review_status", f.review_status);
    if (f.precision !== "All") p.set("precision", f.precision);
    if (f.website) p.set("website", "true");
    if (f.phone) p.set("phone", "true");
    if (String(f.min_branches).trim()) p.set("min_branches", String(f.min_branches));
    return p;
  };

  const refreshStats = useCallback(async (f) => {
    try {
      const p = paramsFromFilters(f);
      const r = await fetch(`${API}/distributor-map/stats?${p.toString()}`);
      const d = await r.json(); setStats(d.data || d);
    } catch { /* ignore */ }
  }, []);

  const renderMarkers = useCallback((rows) => {
    const cluster = clusterRef.current; if (!cluster) return;
    cluster.clearLayers(); markerById.current = {};
    const markers = rows.map((s) => {
      const mk = L.marker([s.latitude, s.longitude], { icon: markerIcon(s, false), title: s.distributorName, keyboard: true, alt: `${s.distributorName}, ${s.city}, ${s.state}` });
      mk.on("click", () => selectBranch(s, mk));
      markerById.current[s.id] = { mk, row: s };
      return mk;
    });
    cluster.addLayers(markers);
    // eslint-disable-next-line
  }, []);

  const doSearch = useCallback(async (override, opts = {}) => {
    const f = override || filters;
    const id = ++reqId.current;
    setStatus("loading");
    try {
      const p = paramsFromFilters(f);
      p.set("include_approximate", f.include_approximate ? "true" : "false");
      p.set("limit", String(f.limit || 1000));
      const r = await fetch(`${API}/distributor-map/search?${p.toString()}`);
      if (!r.ok) throw new Error("bad");
      const d = await r.json(); const data = d.data || d;
      if (id !== reqId.current) return;
      const rows = data.results || [];
      setMeta({ total: data.total, returned: data.returned, placed: data.placed, approximate: data.approximate, unresolved: data.unplaced, companiesMatched: data.companiesMatched });
      renderMarkers(rows);
      setStatus(rows.length ? "ok" : "empty");
      refreshStats(f);
      if (rows.length && clusterRef.current && mapRef.current && opts.fit !== false) {
        try { mapRef.current.fitBounds(clusterRef.current.getBounds().pad(0.15)); } catch { /* ignore */ }
      }
    } catch {
      if (id !== reqId.current) return;
      setStatus("error");
    }
  }, [filters, renderMarkers, refreshStats]);

  useEffect(() => { doSearch(DEFAULT_FILTERS); /* eslint-disable-next-line */ }, []);

  const onStateClick = (feature, layer) => {
    const abbr = STATE_ABBR[feature.properties.name];
    if (!abbr) return;
    if (selectedLayerRef.current && geoLayerRef.current) geoLayerRef.current.resetStyle(selectedLayerRef.current);
    layer.setStyle({ color: "#059669", weight: 2.5, fillColor: "#059669", fillOpacity: 0.12 });
    layer.bringToFront();
    selectedLayerRef.current = layer;
    setSelectedState(feature.properties.name);
    const next = { ...filters, state: abbr };
    setFilters(next);
    try { mapRef.current.fitBounds(layer.getBounds().pad(0.05)); } catch { /* ignore */ }
    doSearch(next, { fit: false });
  };

  // Seller Map-style interaction: clicking a pointer opens a COMPACT popup
  // (desktop) or bottom card (mobile). The full profile opens only after the
  // user clicks "View distributor profile".
  const selectBranch = (row, mk) => {
    // Toggle: clicking the marker whose popup is already open closes it.
    if (!isMobile && openBranchId.current === row.id) {
      mapRef.current.closePopup();
      return;
    }
    setSelectedBranchId(row.id);
    Object.values(markerById.current).forEach(({ mk: m, row: r }) => m.setIcon(markerIcon(r, false)));
    if (mk) mk.setIcon(markerIcon(row, true));
    if (isMobile) { setMobileCard(row); return; }
    const brandCount = (row.companyBrandNames || []).length;
    const addr = clean(row.address);
    const line2 = [row.city, row.state, row.postalCode].filter(Boolean).join(", ");
    const phone = clean(row.phone);
    const gurl = googleUrl({ name: row.distributorName, address: row.address, city: row.city, state: row.state, postalCode: row.postalCode });
    const html = `
      <div class="ez-pop" data-testid="dm-popup">
        <div class="ez-pop-name">${escapeHtml(row.distributorName)}</div>
        ${addr ? `<div class="ez-pop-row">${escapeHtml(addr)}</div>` : ""}
        <div class="ez-pop-row">${escapeHtml(line2 || "—")}</div>
        ${phone ? `<div class="ez-pop-row">${escapeHtml(phone)}</div>` : ""}
        <div class="ez-pop-row">${brandCount} associated brand${brandCount !== 1 ? "s" : ""}</div>
        ${gurl ? `<a class="ez-pop-google" data-testid="dm-popup-google" href="${gurl}" target="_blank" rel="noopener noreferrer" aria-label="Search Google for ${escapeHtml(row.distributorName)}">${GOOGLE_SVG}<span>Search on Google</span></a>` : ""}
        <button class="ez-pop-btn" data-testid="dm-popup-view-profile">View distributor profile</button>
      </div>`;
    if (mk) {
      const pop = L.popup({ closeButton: true, autoPan: true, minWidth: 230, className: "ez-leaflet-pop", autoPanPadding: [50, 70] })
        .setLatLng([row.latitude, row.longitude])
        .setContent(html);
      pop.openOn(mapRef.current); // closes any previous popup (popupclose resets icons + openBranchId)
      Object.values(markerById.current).forEach(({ mk: m, row: r }) => m.setIcon(markerIcon(r, false)));
      mk.setIcon(markerIcon(row, true));
      openBranchId.current = row.id;
      setTimeout(() => {
        const btn = document.querySelector('[data-testid="dm-popup-view-profile"]');
        if (btn) btn.onclick = () => openProfile(row.distributorId, row.id);
      }, 30);
    }
  };

  const openProfile = async (distributorId, branchId) => {
    setMobileCard(null);
    if (mapRef.current) mapRef.current.closePopup(); // fires popupclose -> resets icons + openBranchId
    const entry = markerById.current[branchId];
    if (entry) entry.mk.setIcon(markerIcon(entry.row, true)); // keep selected marker highlighted under the drawer
    setDrawer({ distributorId, selectedBranchId: branchId });
    setProfileTab("overview"); setProfileLoading(true); setProfile(null);
    try {
      const r = await fetch(`${API}/distributor-map/distributor/${distributorId}`);
      if (!r.ok) throw new Error("bad");
      const d = await r.json(); setProfile(d.data || d);
    } catch { toast.error("Could not load distributor profile"); }
    finally { setProfileLoading(false); }
  };

  const clearSelection = () => {
    setSelectedBranchId(null);
    Object.values(markerById.current).forEach(({ mk: m, row: r }) => m.setIcon(markerIcon(r, false)));
  };

  const closeDrawer = () => { setDrawer(null); setProfile(null); clearSelection(); };

  const showBranchOnMap = (branch) => {
    if (branch.latitude == null) { toast("This location has no map coordinates yet."); return; }
    setDrawer(null); setProfile(null);
    const entry = markerById.current[branch.id];
    try { mapRef.current.setView([branch.latitude, branch.longitude], 12); } catch { /* ignore */ }
    if (entry) setTimeout(() => selectBranch(entry.row, entry.mk), 120);
    else setSelectedBranchId(branch.id);
  };

  const reset = () => {
    setFilters(DEFAULT_FILTERS);
    setSelectedState(null); setMobileCard(null); closeDrawer();
    if (selectedLayerRef.current && geoLayerRef.current) { geoLayerRef.current.resetStyle(selectedLayerRef.current); selectedLayerRef.current = null; }
    if (mapRef.current) mapRef.current.setView(US_CENTER, US_ZOOM);
    reqId.current++;
    setTimeout(() => doSearch(DEFAULT_FILTERS), 0);
  };

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const onKey = (e) => { if (e.key === "Enter") doSearch(); };

  const resultLabel = status === "loading" ? "Loading…"
    : status === "ok" ? `${meta.returned} branches · ${meta.companiesMatched} companies${meta.approximate ? ` · ${meta.approximate} approximate` : ""}`
    : status === "empty" ? "0 branches" : "";

  const TOGGLES = [
    { id: "filters", icon: SlidersHorizontal, label: "Search & filters" },
    { id: "help", icon: HelpCircle, label: "Help" },
    { id: "legend", icon: BarChart3, label: "Legend & statistics" },
  ];

  return (
    <div className="ez-sellermap grid" style={{ gridTemplateRows: "auto minmax(0,1fr)", height: "calc(100dvh - 56px)", minWidth: 0, overflow: "hidden" }} data-testid="distributor-map-page">
      <header className="px-[9px] pt-[7px] pb-2 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-stitle text-ink-primary">Distributor Map</h1>
          <p className="text-compact text-ink-secondary">Discover independent distributors, their branch locations, and the brands associated with each company.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="hidden sm:inline text-micro text-ink-muted" aria-live="polite" data-testid="dm-result-count-header">{resultLabel}</span>
          <button data-testid="dm-reset" onClick={reset} className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-ctrl border border-edge-subtle text-micro hover:bg-surface-2"><RotateCcw className="h-3.5 w-3.5" />Reset map</button>
        </div>
      </header>

      <div className="relative mx-[9px] mb-4 rounded-panel border border-edge-subtle overflow-hidden" data-testid="distributor-map-canvas" style={{ minHeight: 0 }}>
        <div ref={mapEl} className="absolute inset-0" />

        {/* top-left toggles */}
        <div ref={toggleGroupRef} className="absolute left-3 top-3 z-[1200] flex flex-col gap-2" data-testid="dm-controls">
          {TOGGLES.map((t) => {
            const Icon = t.icon; const active = openPanel === t.id;
            return (
              <button key={t.id} ref={btnRefs[t.id]} type="button" data-testid={`dm-toggle-${t.id}`}
                aria-label={t.label} aria-expanded={active} aria-controls={`dm-panel-${t.id}`} title={t.label}
                onClick={() => togglePanel(t.id)} className={cn("ez-map-fab", active && "ez-map-fab-active")}>
                <Icon className="h-[18px] w-[18px]" />
              </button>
            );
          })}
        </div>

        {selectedState && (
          <div className="absolute right-14 top-3 z-[700] flex items-center gap-1.5 h-9 px-3 rounded-ctrl bg-surface-0 border border-edge-subtle shadow-float text-micro" data-testid="dm-selected-state">
            <MapPin className="h-3.5 w-3.5 text-emerald-600" /> {selectedState}
            <button aria-label="Clear state" onClick={reset} className="ml-1 h-5 w-5 grid place-items-center rounded hover:bg-surface-2"><X className="h-3.5 w-3.5" /></button>
          </div>
        )}

        {openPanel && (
          <aside ref={panelRef} id={`dm-panel-${openPanel}`} role="dialog" aria-label={TOGGLES.find((t) => t.id === openPanel)?.label} className="ez-map-panel" data-testid={`dm-panel-${openPanel}`}>
            <div className="flex items-center justify-between gap-2 px-3 h-10 border-b border-edge-subtle">
              <span className="text-compact font-medium text-ink-primary">{TOGGLES.find((t) => t.id === openPanel)?.label}</span>
              <button data-testid="dm-panel-close" aria-label="Close panel" onClick={() => { const p = openPanel; setOpenPanel(null); btnRefs[p]?.current?.focus(); }} className="h-7 w-7 grid place-items-center rounded-ctrl hover:bg-surface-2 text-ink-muted"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-3 overflow-auto ez-scroll" style={{ maxHeight: "calc(100% - 40px)" }}>
              {openPanel === "filters" && <FilterPanel filters={filters} set={set} facets={facets} onKey={onKey} onSearch={() => doSearch()} onReset={reset} status={status} resultLabel={resultLabel} />}
              {openPanel === "help" && <HelpPanel />}
              {openPanel === "legend" && <LegendPanel stats={stats} />}
            </div>
          </aside>
        )}

        {status === "loading" && (
          <div className="absolute inset-0 z-[600] grid place-items-center bg-white/30 backdrop-blur-[1px]" data-testid="dm-loading">
            <div className="rounded-panel bg-surface-0/95 border border-edge-subtle shadow-float px-5 py-4 text-center">
              <div className="ez-spin mx-auto mb-2" />
              <div className="text-compact font-medium text-ink-primary">Loading independent distributors…</div>
              <div className="text-micro text-ink-muted">Building geographic clusters from available branch locations.</div>
            </div>
          </div>
        )}
        {status === "empty" && (
          <div className="absolute inset-0 z-[600] grid place-items-center bg-white/50" data-testid="dm-empty">
            <div className="rounded-panel bg-surface-0 border border-edge-subtle shadow-float p-5 max-w-sm text-center">
              <div className="text-compact font-medium text-ink-primary mb-1">No independent distributors match these filters.</div>
              <div className="text-micro text-ink-muted mb-3">Try another state, brand, source, or verification option.</div>
              <button onClick={reset} className="h-8 px-3 rounded-ctrl border border-edge-subtle text-micro hover:bg-surface-2">Reset filters</button>
            </div>
          </div>
        )}
        {status === "error" && (
          <div className="absolute inset-0 z-[600] grid place-items-center bg-white/50" data-testid="dm-error">
            <div className="rounded-panel bg-surface-0 border border-edge-subtle shadow-float p-5 max-w-sm text-center">
              <div className="text-compact font-medium text-ink-primary mb-1">We couldn’t load distributor locations.</div>
              <div className="flex justify-center gap-2 mt-3">
                <button onClick={() => doSearch()} className="h-8 px-3 rounded-ctrl bg-signal-blue text-white text-micro">Retry</button>
                <button onClick={reset} className="h-8 px-3 rounded-ctrl border border-edge-subtle text-micro hover:bg-surface-2">Reset</button>
              </div>
            </div>
          </div>
        )}

      </div>

      {drawer && (
        <DistributorDrawer
          profile={profile} loading={profileLoading} isMobile={isMobile}
          tab={profileTab} setTab={setProfileTab}
          selectedBranchId={drawer.selectedBranchId} onClose={closeDrawer} onShowBranch={showBranchOnMap}
        />
      )}
      {mobileCard && !drawer && isMobile && (
        <MobileCard row={mobileCard}
          onClose={() => { setMobileCard(null); clearSelection(); }}
          onView={() => openProfile(mobileCard.distributorId, mobileCard.id)} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ panels */
function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-micro font-mono uppercase text-ink-muted">{label}</span>
      <div className="relative mt-0.5">
        <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-9 pl-2 pr-7 rounded-ctrl border border-edge-subtle bg-surface-0 text-compact appearance-none focus:outline-none focus:ring-2 focus:ring-signal-blue/40">
          {options.map((o) => { const v = typeof o === "object" ? o.value : o; const l = typeof o === "object" ? o.label : o; return <option key={v} value={v}>{v === "All" ? "- All -" : l}</option>; })}
        </select>
        <ChevronDown className="h-4 w-4 absolute right-2 top-2.5 text-ink-muted pointer-events-none" />
      </div>
    </label>
  );
}
function TextInput({ label, value, onChange, onKey, placeholder, testid, icon: Icon }) {
  return (
    <label className="block">
      <span className="text-micro font-mono uppercase text-ink-muted">{label}</span>
      <div className="mt-0.5 flex items-center h-9 rounded-ctrl border border-edge-subtle bg-surface-0 focus-within:ring-2 focus-within:ring-signal-blue/40">
        {Icon && <Icon className="h-3.5 w-3.5 ml-2 text-ink-muted" />}
        <input data-testid={testid} value={value} onKeyDown={onKey} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full h-full px-2 bg-transparent text-compact focus:outline-none" />
      </div>
    </label>
  );
}
function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 text-compact text-ink-primary cursor-pointer">
      <input type="checkbox" data-testid={testid} checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-emerald-600" />
      {label}
    </label>
  );
}

function FilterPanel({ filters, set, facets, onKey, onSearch, onReset, status, resultLabel }) {
  return (
    <div className="space-y-3" role="search">
      <TextInput label="Distributor name" testid="dm-filter-name" value={filters.name} onChange={(v) => set("name", v)} onKey={onKey} placeholder="Company or branch name" icon={Search} />
      <TextInput label="Brand name" testid="dm-filter-brand-text" value={filters.brand === "All" ? "" : filters.brand} onChange={(v) => set("brand", v || "All")} onKey={onKey} placeholder="Brand (e.g. Mondelez)" />
      <Select label="Brand from list" testid="dm-filter-brand" value={filters.brand} onChange={(v) => set("brand", v)} options={["All", ...facets.brands]} />
      <Select label="Brand relationship status" testid="dm-filter-brand-status" value={filters.brand_status} onChange={(v) => set("brand_status", v)} options={["All", ...facets.brandStatuses.map((s) => ({ value: s, label: STATUS_LABEL[s] || s }))]} />
      <Select label="State" testid="dm-filter-state" value={filters.state} onChange={(v) => set("state", v)} options={["All", ...facets.states]} />
      <TextInput label="City" testid="dm-filter-city" value={filters.city} onChange={(v) => set("city", v)} onKey={onKey} placeholder="City" />
      <TextInput label="ZIP code" testid="dm-filter-zip" value={filters.zip} onChange={(v) => set("zip", v)} onKey={onKey} placeholder="ZIP prefix" />
      <Select label="Source list" testid="dm-filter-source" value={filters.source} onChange={(v) => set("source", v)} options={["All", ...facets.sources]} />
      <Select label="Contact verification" testid="dm-filter-contact" value={filters.contact_verification} onChange={(v) => set("contact_verification", v)} options={["All", ...facets.contactVerifications]} />
      <Select label="Review status" testid="dm-filter-review" value={filters.review_status} onChange={(v) => set("review_status", v)} options={["All", ...facets.reviewStatuses]} />
      <Select label="Location accuracy" testid="dm-filter-precision" value={filters.precision} onChange={(v) => set("precision", v)} options={["All", ...facets.precisions.map((p) => ({ value: p, label: PRECISION_LABEL[p] || p }))]} />
      <label className="block">
        <span className="text-micro font-mono uppercase text-ink-muted">Minimum branches</span>
        <input type="number" data-testid="dm-filter-min-branches" value={filters.min_branches} onKeyDown={onKey} onChange={(e) => set("min_branches", e.target.value)} placeholder="e.g. 2" className="mt-0.5 w-full h-9 px-2 rounded-ctrl border border-edge-subtle bg-surface-0 text-compact focus:outline-none focus:ring-2 focus:ring-signal-blue/40" />
      </label>
      <div className="space-y-2 pt-1">
        <Toggle label="Has website" testid="dm-filter-has-website" checked={filters.website} onChange={(v) => set("website", v)} />
        <Toggle label="Has phone" testid="dm-filter-has-phone" checked={filters.phone} onChange={(v) => set("phone", v)} />
        <Toggle label="Include approximate locations" testid="dm-filter-approx" checked={filters.include_approximate} onChange={(v) => set("include_approximate", v)} />
      </div>
      <label className="block" title="Limits returned branch locations — not the total number of companies.">
        <span className="text-micro font-mono uppercase text-ink-muted">Maximum results</span>
        <input type="number" data-testid="dm-filter-limit" value={filters.limit} onKeyDown={onKey} onChange={(e) => set("limit", Math.max(1, parseInt(e.target.value || "1000", 10)))} className="mt-0.5 w-full h-9 px-2 rounded-ctrl border border-edge-subtle bg-surface-0 text-compact focus:outline-none focus:ring-2 focus:ring-signal-blue/40" />
        <span className="text-micro text-ink-muted">Applies to returned branch locations, not total companies.</span>
      </label>
      <div className="flex items-center gap-2 pt-1">
        <button data-testid="dm-search" disabled={status === "loading"} onClick={onSearch} className={cn("flex-1 inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-ctrl text-compact font-medium", status === "loading" ? "bg-signal-blue/40 text-white" : "bg-signal-blue text-white hover:bg-signal-blue/90")}><Search className="h-4 w-4" />Search</button>
        <button data-testid="dm-reset-filters" onClick={onReset} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-ctrl border border-edge-subtle text-compact hover:bg-surface-2">Reset</button>
      </div>
      <div className="text-micro text-ink-muted" aria-live="polite" data-testid="dm-result-count">{resultLabel}</div>
    </div>
  );
}

function HelpPanel() {
  return (
    <div className="space-y-2.5 text-compact text-ink-secondary" data-testid="dm-help-content">
      <ul className="list-disc pl-4 space-y-1.5">
        <li>Markers represent <b>branch records</b>, not necessarily headquarters.</li>
        <li>Multiple markers may belong to the <b>same distributor</b>.</li>
        <li>Some locations are <b>approximate</b> (amber) — placed at the ZIP or state centroid.</li>
        <li>Click a pointer for a compact summary, then <b>View distributor profile</b> for full details.</li>
        <li>Company inclusion does <b>not</b> automatically confirm which brands or products are sold.</li>
        <li>Brand cards show the <b>strength of the available brand relationship</b>.</li>
        <li>Verify contact info, product availability, and authorization before doing business.</li>
      </ul>
      <p className="text-micro text-ink-muted">Data imported from the distributor dataset. No brands, products, or coordinates are invented.</p>
    </div>
  );
}

function StatRow({ label, value, dot }) {
  return (
    <div className="flex items-center justify-between text-compact py-0.5">
      <span className="flex items-center gap-2 text-ink-secondary">{dot && <span className={dot} />}{label}</span>
      <span className="ident tabular-nums text-ink-primary">{num(value)}</span>
    </div>
  );
}
function LegendPanel({ stats }) {
  if (!stats) return <div className="text-compact text-ink-muted py-6 text-center">Loading statistics…</div>;
  return (
    <div className="space-y-4" data-testid="dm-legend-content">
      <div>
        <div className="text-micro font-mono uppercase text-ink-muted mb-1.5">Legend</div>
        <div className="space-y-1">
          <StatRow label="Distributor branch" value={stats.resolved} dot="ez-legend-seller" />
          <StatRow label="Approximate location" value={stats.approximate} dot="ez-legend-approx" />
          <div className="flex items-center gap-2 text-compact text-ink-secondary"><span className="ez-legend-cluster" />Cluster (branch count)</div>
          <div className="flex items-center gap-2 text-compact text-ink-secondary"><span className="ez-legend-seller sel" />Selected branch</div>
          <div className="flex items-center gap-2 text-compact text-ink-secondary"><span className="ez-legend-review" />Location requiring review</div>
        </div>
      </div>
      <div>
        <div className="text-micro font-mono uppercase text-ink-muted mb-1.5">Dataset (matching filters)</div>
        <StatRow label="Independent companies" value={stats.companies} />
        <StatRow label="Companies matching filters" value={stats.companiesMatched} />
        <StatRow label="Branch locations" value={stats.branches} />
        <StatRow label="Visible branches" value={stats.visibleBranches} />
        <StatRow label="States represented" value={stats.states} />
        <StatRow label="Brands represented" value={stats.brands} />
      </div>
      <div>
        <div className="text-micro font-mono uppercase text-ink-muted mb-1.5">Brand relationships</div>
        <StatRow label="Verified seller" value={stats.verifiedSeller} />
        <StatRow label="Listed in brand verified list" value={stats.listedByBrand} />
        <StatRow label="Needs verification" value={stats.needsVerification} />
      </div>
      <div>
        <div className="text-micro font-mono uppercase text-ink-muted mb-1.5">Location accuracy</div>
        <StatRow label="Resolved (exact)" value={stats.resolved} />
        <StatRow label="Approximate" value={stats.approximate} />
        <StatRow label="Pending" value={stats.pending} />
        <StatRow label="Unresolved" value={stats.unresolved} />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ drawer + profile */
const TABS = [["overview", "Overview"], ["brands", "Brands Sold"], ["locations", "Locations"], ["contact", "Contact Information"]];

function MobileCard({ row, onClose, onView }) {
  const brandCount = (row.companyBrandNames || []).length;
  const phone = clean(row.phone);
  return createPortal(
    <div className="fixed inset-x-0 bottom-0 z-[2000] p-3" data-testid="dm-mobile-card">
      <div className="ez-bottomsheet !max-h-none rounded-2xl border border-edge-subtle p-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-compact font-semibold text-ink-primary">{row.distributorName}</div>
            <div className="text-micro text-ink-muted">{[clean(row.address), row.city, row.state, row.postalCode].filter(Boolean).join(", ") || "—"}</div>
          </div>
          <button data-testid="dm-mobile-card-close" onClick={onClose} aria-label="Close" className="h-9 w-9 grid place-items-center rounded-ctrl border border-edge-subtle shrink-0"><X className="h-4 w-4" /></button>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-micro text-ink-secondary">
          {phone && <span>{phone}</span>}
          <span>{brandCount} associated brand{brandCount !== 1 ? "s" : ""}</span>
        </div>
        <button data-testid="dm-mobile-view-profile" onClick={onView} className="mt-3 w-full h-11 rounded-ctrl bg-navy-900 text-white text-compact font-medium">View distributor profile</button>
        <GoogleSearchButton branch={row} name={row.distributorName} className="mt-2 w-full justify-center h-11" testid="dm-mobile-google" />
      </div>
    </div>, document.body);
}

function DistributorDrawer({ profile, loading, isMobile, tab, setTab, selectedBranchId, onClose, onShowBranch }) {
  useEffect(() => {
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [onClose]);

  const p = profile;
  const selectedBranch = p?.branches?.find((b) => b.id === selectedBranchId) || p?.branches?.[0];

  const body = loading || !p ? (
    <div className="p-4 space-y-2">{[...Array(6)].map((_, i) => <div key={i} className="h-8 rounded bg-surface-2 animate-pulse" />)}</div>
  ) : (
    <FullProfile p={p} tab={tab} setTab={setTab} onShowBranch={onShowBranch} selectedBranch={selectedBranch} />
  );

  const header = (
    <div className="px-4 py-3 border-b border-edge-subtle">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-stitle text-ink-primary truncate" data-testid="dm-drawer-name">{p?.companyName || "Distributor"}</div>
          {selectedBranch && <div className="text-micro text-ink-muted truncate">Selected: {[selectedBranch.address, selectedBranch.city, selectedBranch.state, selectedBranch.postalCode].filter(Boolean).join(", ") || "—"}</div>}
        </div>
        <button data-testid="dm-drawer-close" onClick={onClose} aria-label="Close distributor panel" className="h-8 w-8 grid place-items-center rounded-ctrl border border-edge-subtle hover:bg-surface-2 shrink-0"><X className="h-4 w-4" /></button>
      </div>
      {p && <GoogleSearchButton branch={selectedBranch || {}} name={p.companyName} className="mt-2.5" testid="dm-header-google" />}
    </div>
  );

  if (isMobile) {
    return createPortal(
      <div className="fixed inset-0 z-[2000] bg-black/30" onClick={onClose}>
        <div className="ez-bottomsheet !max-h-[96dvh]" style={{ top: 8 }} role="dialog" aria-modal="true" aria-label="Distributor profile" onClick={(e) => e.stopPropagation()} data-testid="dm-bottom-sheet">
          <div className="ez-sheet-grip" />
          {header}
          <div className="flex-1 overflow-auto ez-scroll">{body}</div>
        </div>
      </div>, document.body);
  }

  return createPortal(
    <div className="fixed right-0 top-14 bottom-0 z-[2000] bg-surface-0 border-l border-edge-subtle shadow-float flex flex-col ez-rise-in"
      style={{ width: "min(720px, calc(100vw - 24px))" }}
      role="dialog" aria-label="Distributor profile" data-testid="dm-drawer">
      {header}
      <div className="flex-1 overflow-auto ez-scroll">{body}</div>
    </div>, document.body);
}

function Field({ label, value }) {
  const v = clean(value);
  return <div><div className="text-micro font-mono uppercase text-ink-muted">{label}</div><div className="text-compact text-ink-primary break-words">{v == null ? "—" : (isUrl(v) ? <a href={v} target="_blank" rel="noopener noreferrer" className="text-signal-blue hover:underline inline-flex items-center gap-1">{v}<ExternalLink className="h-3 w-3" /></a> : v)}</div></div>;
}

function BrandCard({ r }) {
  return (
    <div className="rounded-ctrl border border-edge-subtle p-3 flex items-center justify-between gap-2" data-testid="dm-brand-card">
      <div className="min-w-0">
        <div className="text-compact font-semibold text-ink-primary truncate">{r.brandName}</div>
        {clean(r.productCategory) && <div className="text-micro text-ink-secondary">{r.productCategory}</div>}
        {r.products?.length > 0 && <div className="text-micro text-ink-secondary">{r.products.join(", ")}</div>}
      </div>
      <span className={cn("ez-brand-badge shrink-0", BADGE_CLASS[r.status])}>{STATUS_LABEL[r.status] || r.status}</span>
    </div>
  );
}

function BrandsSold({ brands }) {
  const [q, setQ] = useState("");
  const [sf, setSf] = useState("All");
  const filtered = (brands || []).filter((r) => (!q || r.brandName.toLowerCase().includes(q.toLowerCase())) && (sf === "All" || r.status === sf));
  return (
    <div data-testid="dm-brands-sold">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-compact font-semibold text-ink-primary">Brands Sold by This Distributor</h3>
        <span className="text-micro text-ink-muted">{brands.length} total</span>
      </div>
      {brands.length === 0 ? (
        <div className="rounded-ctrl border border-dashed border-edge-subtle p-4 text-center" data-testid="dm-brands-empty">
          <span className="ez-brand-badge ez-badge-needs mb-2 inline-flex">Needs verification</span>
          <div className="text-compact font-medium text-ink-primary">Brands not yet verified</div>
          <div className="text-micro text-ink-muted mt-1">Brand coverage has not yet been confirmed for this distributor.</div>
        </div>
      ) : (
        <div className="space-y-2">
          {brands.length > 3 && (
            <div className="flex gap-2">
              <input data-testid="dm-brand-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search brands" className="flex-1 h-8 px-2 rounded-ctrl border border-edge-subtle bg-surface-0 text-compact focus:outline-none" />
              <select data-testid="dm-brand-status-filter" value={sf} onChange={(e) => setSf(e.target.value)} className="h-8 px-2 rounded-ctrl border border-edge-subtle bg-surface-0 text-compact">
                <option value="All">All statuses</option>
                {Object.entries(STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          )}
          {filtered.map((r) => <BrandCard key={r.id} r={r} />)}
        </div>
      )}
    </div>
  );
}

function Table({ cols, rows, empty, testid }) {
  if (!rows.length) return <div className="text-compact text-ink-muted py-6 text-center">{empty}</div>;
  return (
    <div className="overflow-x-auto ez-scroll border border-edge-subtle rounded-ctrl" data-testid={testid}>
      <table className="w-full text-compact" style={{ minWidth: cols.length * 110 }}>
        <thead><tr className="text-micro text-ink-muted text-left bg-surface-2">{cols.map((c) => <th key={c} className="px-3 py-2 border-b border-edge-subtle whitespace-nowrap">{c}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i} className="hover:bg-surface-1">{r.map((v, j) => <td key={j} className="px-3 py-2 border-b border-edge-subtle whitespace-nowrap">{v == null || v === "" ? "—" : v}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

function FullProfile({ p, tab, setTab, onShowBranch, selectedBranch }) {
  return (
    <div data-testid="dm-full-profile">
      <div className="px-4 pt-2 border-b border-edge-subtle flex gap-1 overflow-x-auto ez-scroll sticky top-0 bg-surface-0 z-10">
        {TABS.map(([id, lbl]) => (
          <button key={id} data-testid={`dm-tab-${id}`} onClick={() => setTab(id)} className={cn("px-3 h-9 text-compact whitespace-nowrap border-b-2 -mb-px", tab === id ? "border-signal-blue text-signal-blue font-medium" : "border-transparent text-ink-muted hover:text-ink-primary")}>{lbl}</button>
        ))}
      </div>
      <div className="p-4" data-testid="dm-full-body">
        {tab === "overview" && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Field label="Company name" value={p.companyName} />
            <Field label="Unique branch locations" value={p.branchCount} />
            <Field label="States count" value={p.statesCount} />
            <Field label="States represented" value={(p.statesSeen || []).join(", ")} />
            <Field label="Phone numbers" value={(p.phones || []).join(", ")} />
            <Field label="Websites" value={(p.websites || []).join(", ")} />
            <Field label="Supported brands" value={(p.brands || []).length} />
          </div>
        )}
        {tab === "brands" && <BrandsSold brands={p.brands || []} />}
        {tab === "locations" && (
          <Table testid="dm-locations-table"
            cols={["Address", "City", "State", "ZIP", "Phone", "Website", "Location accuracy", ""]}
            rows={(p.branches || []).map((b) => [
              b.address, b.city, b.state, b.postalCode, b.phone,
              isUrl(b.website) ? <a href={b.website} target="_blank" rel="noopener noreferrer" className="text-signal-blue hover:underline">link</a> : b.website,
              PRECISION_LABEL[b.geocodingPrecision] || b.geocodingPrecision,
              <button key="s" data-testid="dm-show-on-map" onClick={() => onShowBranch(b)} className="inline-flex items-center gap-1 text-micro text-signal-blue hover:underline"><Crosshair className="h-3 w-3" />Show on map</button>,
            ])} empty="No branch locations." />
        )}
        {tab === "contact" && (
          <div className="space-y-4" data-testid="dm-contact-body">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone numbers" value={(p.phones || []).join(", ")} />
              <Field label="Websites" value={(p.websites || []).join(", ")} />
            </div>
            <div>
              <div className="text-micro font-mono uppercase text-ink-muted mb-1.5">Branch contacts</div>
              <Table cols={["Location", "Phone", "Website"]}
                rows={(p.branches || []).map((b) => [
                  [b.city, b.state, b.postalCode].filter(Boolean).join(", "),
                  (b.phones || [b.phone]).filter(Boolean).join(", ") || null,
                  isUrl(b.website) ? <a href={b.website} target="_blank" rel="noopener noreferrer" className="text-signal-blue hover:underline">link</a> : (b.websites || []).find(isUrl) ? <a href={(b.websites || []).find(isUrl)} target="_blank" rel="noopener noreferrer" className="text-signal-blue hover:underline">link</a> : null,
                ])} empty="No branch contacts." />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function escapeHtml(s) {
  return String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

// Multicolor Google "G" as a raw SVG string for the Leaflet (HTML) popup.
const GOOGLE_SVG = `<svg viewBox="0 0 48 48" width="15" height="15" aria-hidden="true" style="flex:0 0 auto"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>`;

// Reusable "Search on Google" button — opens a normal Google search in a new tab
// built from the SELECTED branch's verified fields (no API, no placeholders).
function GoogleSearchButton({ branch, name, className = "", testid = "dm-google" }) {
  const url = googleUrl({ name, address: branch?.address, city: branch?.city, state: branch?.state, postalCode: branch?.postalCode });
  if (!url) return null;
  const label = `Search Google for ${name}${branch?.city ? ` at ${branch.city}, ${branch.state || ""}` : ""}`;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" data-testid={testid} aria-label={label}
      className={cn("inline-flex items-center gap-2 h-9 px-3 rounded-ctrl border border-edge-subtle bg-white text-ink-primary text-compact font-medium cursor-pointer transition-colors hover:bg-surface-1 active:bg-surface-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-signal-blue/50", className)}>
      <GoogleIcon className="h-4 w-4" />
      <span>Search on Google</span>
    </a>
  );
}
